/**
 * LegendControl Component
 *
 * Main collapsible legend panel for EasyMap 3.
 * Displays legends for all enabled layers that have legend capability.
 * Positioned bottom-right of the map.
 * Panel is draggable by its header.
 */

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useLayerDrawer } from '../../layerDrawer';
import { getActiveLegends } from './legendModel';
import LegendItem from './LegendItem';
import './LegendControl.scss';

/** Initial position relative to bottom-right corner */
const INITIAL_POSITION = { x: 0, y: 0 };
/** Initial size of the legend panel */
const INITIAL_SIZE = { width: 280, height: 400 };
/** Minimum size constraints */
const MIN_SIZE = { width: 200, height: 150 };
/** Maximum size constraints (keep the panel on-screen) */
const MAX_SIZE = { width: 640, height: 800 };

const LegendControl: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getEnabledLayers } = useLayerDrawer();

  // Position state for draggable panel (offset from initial position)
  const [position, setPosition] = useState(INITIAL_POSITION);

  // Size state for resizable panel
  const [size, setSize] = useState(INITIAL_SIZE);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionStartRef = useRef({ x: 0, y: 0 });

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, y: 0 });
  const sizeStartRef = useRef({ width: 0, height: 0 });

  // Derive a complete legend for every active layer (not just WMS) via the
  // shared legend model, excluding backgrounds.
  const legendLayers = useMemo(() => getActiveLegends(getEnabledLayers()), [getEnabledLayers]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Drag handlers
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Only start drag on left mouse button
    if (e.button !== 0) return;

    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionStartRef.current = { x: position.x, y: position.y };
  }, [position]);

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    setPosition({
      x: positionStartRef.current.x + deltaX,
      y: positionStartRef.current.y + deltaY,
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = { x: e.clientX, y: e.clientY };
    sizeStartRef.current = { width: size.width, height: size.height };
  }, [size]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    // Calculate delta (resize from top-left corner, so invert deltas)
    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;

    // Apply new size with minimum constraints
    // Since panel is anchored bottom-right, dragging left increases width, dragging up increases height
    setSize({
      width: Math.min(MAX_SIZE.width, Math.max(MIN_SIZE.width, sizeStartRef.current.width - deltaX)),
      height: Math.min(MAX_SIZE.height, Math.max(MIN_SIZE.height, sizeStartRef.current.height - deltaY)),
    });
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Attach/detach document-level mouse listeners for drag and resize
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
    }
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isDragging, isResizing, handleDragMove, handleDragEnd, handleResizeMove, handleResizeEnd]);

  // Don't render toggle button if no legend-capable layers are enabled
  if (legendLayers.length === 0) {
    return null;
  }

  return (
    <div className={`legend-control ${isExpanded ? 'legend-control--expanded' : ''}`}>
      {/* Toggle Button */}
      <button
        className="legend-control__toggle control-button"
        onClick={handleToggle}
        title={isExpanded ? 'Hide Legend' : 'Show Legend'}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="currentColor"
        >
          {/* Legend/list icon */}
          <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
        </svg>
        <span className="legend-control__badge">{legendLayers.length}</span>
      </button>

      {/* Legend Panel */}
      {isExpanded && (
        <div
          className={`legend-control__panel ${isDragging ? 'legend-control__panel--dragging' : ''} ${isResizing ? 'legend-control__panel--resizing' : ''}`}
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            width: `${size.width}px`,
            height: `${size.height}px`,
          }}
        >
          {/* Resize handle - top-left corner */}
          <div
            className="legend-control__resize-handle"
            onMouseDown={handleResizeStart}
            title="Drag to resize"
          />
          <div
            className="legend-control__header legend-control__header--draggable"
            onMouseDown={handleDragStart}
          >
            <span className="legend-control__title">Map Legend</span>
            <button
              className="legend-control__close"
              onClick={handleToggle}
              title="Close Legend"
            >
              &times;
            </button>
          </div>
          <div className="legend-control__content">
            {legendLayers.map((legend) => (
              <LegendItem key={legend.layerId} legend={legend} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LegendControl;
